import React, { useState, useEffect, useCallback } from 'react';
import { JobList } from '../components/jobs/JobList';
import { JobForm } from '../components/jobs/JobForm';
import { JobDetail } from '../components/jobs/JobDetail';
import { JobStats } from '../components/jobs/JobStats';
import { ConvertToInvoiceForm } from '../components/jobs/ConvertToInvoiceForm';
import { JobFilters, Job } from '../types/job';

type PageMode = 'list' | 'create' | 'edit' | 'view';

export const JobsPage: React.FC = () => {
  const [mode, setMode] = useState<PageMode>('list');
  
  // Debug mode changes
  useEffect(() => {
    console.log('Mode changed to:', mode);
  }, [mode]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showConvertForm, setShowConvertForm] = useState(false);
  const [filters, setFilters] = useState<JobFilters>({
    page: 1,
    limit: 10
  });

  const handleCreateJob = () => {
    setSelectedJob(null);
    setMode('create');
  };

  const handleViewJob = (job: Job) => {
    setSelectedJob(job);
    setMode('view');
  };

  const handleEditJob = (job: Job) => {
    setSelectedJob(job);
    setMode('edit');
  };

  const handleBackToList = () => {
    console.log('handleBackToList called - resetting selectedJob to null');
    setMode('list');
    setSelectedJob(null);
    // Also close convert form if open
    setShowConvertForm(false);
  };

  const handleConvertToInvoice = useCallback((job: Job) => {
    console.log('handleConvertToInvoice called with job:', job);
    // Set both states together to avoid timing issues
    setSelectedJob(job);
    setShowConvertForm(true);
    console.log('Modal state set: showConvertForm=true, selectedJob=', job);
  }, []);

  // Debug state changes
  useEffect(() => {
    console.log('State changed - showConvertForm:', showConvertForm, 'selectedJob:', selectedJob?.id);
    console.log('Modal render condition:', showConvertForm && selectedJob, 'showConvertForm:', showConvertForm, 'selectedJob exists:', !!selectedJob);
  }, [showConvertForm, selectedJob]);

  const handleConvertSuccess = () => {
    console.log('handleConvertSuccess called - closing modal');
    setShowConvertForm(false);
    setSelectedJob(null);
  };

  const handleConvertCancel = () => {
    console.log('handleConvertCancel called - closing modal');
    setShowConvertForm(false);
    setSelectedJob(null);
  };

  const handleFilterChange = (newFilters: Partial<JobFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  // Show create form
  if (mode === 'create') {
    return (
      <div style={{ padding: 24 }}>
        <JobForm
          onSuccess={handleBackToList}
          onCancel={handleBackToList}
        />
      </div>
    );
  }

  // Show edit form
  if (mode === 'edit' && selectedJob) {
    return (
      <div style={{ padding: 24 }}>
        <JobForm
          job={selectedJob}
          onSuccess={handleBackToList}
          onCancel={handleBackToList}
        />
      </div>
    );
  }

  // Show job detail view
  if (mode === 'view' && selectedJob) {
    return (
      <>
        <JobDetail
          jobId={selectedJob.id}
          onEdit={handleEditJob}
          onBack={handleBackToList}
          onConvertToInvoice={handleConvertToInvoice}
        />
        {/* Convert to Invoice Form Modal */}
        {(() => {
          console.log('Modal render check (view mode) - showConvertForm:', showConvertForm, 'selectedJob:', selectedJob?.id);
          if (showConvertForm && selectedJob) {
            console.log('About to render ConvertToInvoiceForm with job:', selectedJob);
            try {
              return (
                <ConvertToInvoiceForm
                  key={`convert-${selectedJob.id}`}
                  job={selectedJob}
                  onSuccess={handleConvertSuccess}
                  onCancel={handleConvertCancel}
                />
              );
            } catch (error) {
              console.error('Error rendering ConvertToInvoiceForm:', error);
              return <div>Error loading convert form</div>;
            }
          }
          return null;
        })()}
      </>
    );
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '9px 12px', background: '#f4efe2',
    border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
    fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
    outline: 'none', colorScheme: 'light' as any, boxSizing: 'border-box' as const,
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 9,
    fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 6,
  };

  // Show jobs list
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Header */}
      <div style={{ paddingBottom: 20, borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 400, letterSpacing: '0.4em', textTransform: 'uppercase', color: '#8a8778', marginBottom: 8 }}>
            Operations
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 36, fontWeight: 300, color: '#1c2033', lineHeight: 1 }}>
            Jobs
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 300, color: '#8a8778', marginTop: 8 }}>
            Manage customer projects and jobs
          </div>
        </div>
        <button
          onClick={handleCreateJob}
          style={{
            fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 500,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            background: 'linear-gradient(135deg, #d97706, #b45309)',
            color: '#1c2033', border: 'none', padding: '12px 24px',
            borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 32px rgba(217,119,6,0.25)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none'; }}
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Job
        </button>
      </div>

      <JobStats />

      {/* Filters */}
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 16 }}>
          <div>
            <label style={labelStyle}>Search</label>
            <input
              type="text"
              placeholder="Search jobs..."
              value={filters.search || ''}
              onChange={(e) => handleFilterChange({ search: e.target.value })}
              style={inputStyle}
              onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(217,119,6,0.35)'; }}
              onBlur={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(92,78,51,0.25)'; }}
            />
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select
              value={filters.status || ''}
              onChange={(e) => handleFilterChange({ status: e.target.value as any })}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="COMPLETED">Completed</option>
              <option value="ON_HOLD">On Hold</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Items per page</label>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange({ limit: parseInt(e.target.value) })}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={() => setFilters({ page: 1, limit: 10 })}
              style={{
                padding: '9px 16px', background: 'none',
                border: '1px solid rgba(92,78,51,0.22)', borderRadius: 4,
                fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase',
                color: '#6b6a5c', cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <JobList 
        filters={filters} 
        onView={handleViewJob}
        onEdit={handleEditJob}
      />

      {/* Convert to Invoice Form Modal */}
      {(() => {
        console.log('Modal render check - showConvertForm:', showConvertForm, 'selectedJob:', selectedJob?.id);
        if (showConvertForm && selectedJob) {
          console.log('About to render ConvertToInvoiceForm with job:', selectedJob);
          try {
            return (
              <ConvertToInvoiceForm
                key={`convert-${selectedJob.id}`}
                job={selectedJob}
                onSuccess={handleConvertSuccess}
                onCancel={handleConvertCancel}
              />
            );
          } catch (error) {
            console.error('Error rendering ConvertToInvoiceForm:', error);
            return <div>Error loading convert form</div>;
          }
        }
        return null;
      })()}
    </div>
  );
};